import React, {ReactElement, useCallback, useEffect, useState} from 'react';

//css
import "./calendrier.css"

//devExtrem
import { Button } from 'devextreme-react';

//date
import {
  addMonths,
  subMonths,
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  setMonth,
} from 'date-fns';
import { frCA, enCA } from 'date-fns/locale';

//hooks
import useDateSelection from '../../../hooks/gestion-plans/useDateSelection';

//models
import ICalendarCustomProps from '../../../models/ICustomCalendar';

const CalendarCustom: React.FC<ICalendarCustomProps> = ({ locale = 'fr', currentYear, currentMonth, dataSource, disabled, onValueMonthChange, onValueYearChange }): ReactElement => {
  // Sélectionner la localisation en fonction de la langue choisie
  const currentLocale = locale === 'fr' ? frCA : enCA;
  
  // États du composant
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
  const { handleDateHover, handleSelectDate } = useDateSelection(currentDate, selectedDates, setSelectedDates);

  // useEffect -- Met à jour le calendrier en fonction du mois et de l'année changés dans les champs input
  useEffect(() => {
    // Créer une liste de mois en français
    const monthOptions = Array.from({ length: 12 }, (_, index) => format(setMonth(new Date(), index), "MMMM", { locale: frCA }));
    // Trouver l'index du mois actuel
    const monthIndex = monthOptions.findIndex((m) => m.toLowerCase() === currentMonth.toLowerCase());
    // Si le mois et l'année sont valides, mettre à jour la date courante
    if (!isNaN(monthIndex) && !isNaN(currentYear)) {
      setCurrentDate(new Date(currentYear, monthIndex));
    } else {
      console.log("Invalid currentMonth or currentYear");
    }
  }, [currentMonth, currentYear]);

  // useEffect -- Met à jour le calendrier en fonction du dataSource qui change
  useEffect(() => {
    updateCalendarFromDateArray()
  }, [dataSource]);

  const updateCalendarFromDateArray = useCallback(() => {
    if (dataSource.length > 0) {
      // Extraire les dates de début et de fin du dataSource
      const { dateDebut, dateFin } = dataSource[0];
      const convertDateDebut = dateDebut as unknown as Date;
      const convertDateFin = dateFin as unknown as Date;
      // Mettre à jour le mois et l'année affichés
      // onValueMonthChange(format(convertDateDebut, 'MMMM', { locale: currentLocale }));
      // onValueYearChange(parseInt(format(convertDateDebut, 'yyyy', { locale: currentLocale }), 10));
      // Mettre à jour la date courante et les dates sélectionnées
      // setCurrentDate(new Date(currentYear, convertDateDebut.getMonth()));
      setSelectedDates(onSelectDatesBetween(convertDateDebut, convertDateFin));
    } else {
      // Réinitialiser le calendrier si le dataSource est vide
      setSelectedDates([new Date()]);
    }
  },[dataSource])
  // Fonction -- Sélectionne toutes les dates entre deux dates données
  const onSelectDatesBetween = (startDate: Date, endDate: Date) => {
    const dates = [];
    let currentDate = startDate;
    while (currentDate <= endDate) {
      dates.push(currentDate);
      currentDate = addDays(currentDate, 1);
    }
    return dates;
  };

  // Fonction -- Permet la navigation arrière
  const handlePrevMonth = () => {
    const newDate = subMonths(currentDate, 1);
    setCurrentDate(newDate);
    setDirection('prev');
    onValueMonthChange(format(newDate, 'MMMM', { locale: currentLocale }));
    onValueYearChange(parseInt(format(newDate, 'yyyy', { locale: currentLocale }), 10));
  };

  // Fonction -- Permet la navigation avant
  const handleNextMonth = () => {
    const newDate = addMonths(currentDate, 1);
    setCurrentDate(newDate);
    setDirection('next');
    onValueMonthChange(format(newDate, 'MMMM', { locale: currentLocale }));
    onValueYearChange(parseInt(format(newDate, 'yyyy', { locale: currentLocale }), 10));
  };

  // Fonction -- Sélectionne toutes les dates d'un mois par le clic sur le mois
  const handleSelectMonth = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const allDates = [];
    for (let date = monthStart; date <= monthEnd; date = addDays(date, 1)) {
      allDates.push(date);
    }
    // Vérifie si toutes les dates du mois sont déjà sélectionnées
    if (allDates.every(date => selectedDates.some(selectedDate => isSameDay(selectedDate, date)))) {
      // Désélectionner les dates du mois
      setSelectedDates(selectedDates.filter(date => !isSameMonth(date, currentDate)));
    } else {
      // Sélectionner toutes les dates du mois
      const newSelectedDates = selectedDates.filter(date => !isSameMonth(date, currentDate)).concat(allDates);
      setSelectedDates(newSelectedDates);
    }
  };

  // Fonction -- Sélectionne toutes les dates d'un jour de la semaine par le clic sur le jour de la semaine
  const handleSelectWeekday = (weekdayIndex: number) => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const allDates = [];
    for (let date = monthStart; date <= monthEnd; date = addDays(date, 1)) {
      if (date.getDay() === weekdayIndex) {
        allDates.push(date);
      }
    }
    // Vérifie si toutes les dates du jour de la semaine sont déjà sélectionnées
    if (allDates.every(date => selectedDates.some(selectedDate => isSameDay(selectedDate, date)))) {
      // Désélectionner les dates du jour de la semaine
      setSelectedDates(selectedDates.filter(date => !(date.getDay() === weekdayIndex && isSameMonth(date, currentDate))));
    } else {
      // Sélectionner toutes les dates du jour de la semaine
      const newSelectedDates = selectedDates.filter(date => !(date.getDay() === weekdayIndex && isSameMonth(date, currentDate))).concat(allDates);
      setSelectedDates(newSelectedDates);
    }
  };

  // Fonction -- Génère le calendrier
  const generateCalendar = () => {
    const startDate = startOfWeek(startOfMonth(currentDate), { locale: currentLocale });
    const endDate = endOfWeek(endOfMonth(currentDate), { locale: currentLocale });
    const rows = [];
    let days = [];
    let day = startDate;
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        days.push(day);
        day = addDays(day, 1);
      }
      rows.push(days);
      days = [];
    }
    return rows;
  };

  // Fonction -- Réinitialise le calendrier dans son état initial
  const handleReset = () => {
    setCurrentDate(new Date());
    setSelectedDates([new Date()]);
    onValueYearChange((new Date().getFullYear()));
    onValueMonthChange(format(new Date(), "MMMM", { locale: currentLocale }));
  };

  // Générer les rangées du calendrier
  const calendarRows = generateCalendar();
  // Générer les jours de la semaine en français
  const daysOfWeek = Array.from({ length: 7 }, (_, i) => format(addDays(startOfWeek(new Date(), { locale: currentLocale }), i), 'EEE', { locale: currentLocale }));

  return (
    <div className={`calendrier-plans-container ${disabled ? 'disabled-calendar' : ''}`}>
      <div className="calendar">
        <div className="header">
          <button onClick={handlePrevMonth}>&lt;</button>
          <span onClick={handleSelectMonth}>{format(currentDate, 'MMMM yyyy', { locale: currentLocale })}</span>
          <button onClick={handleNextMonth}>&gt;</button>
        </div>

        <div className="days">
          {daysOfWeek.map((day, index) => (
            <div
              key={index}
              className="day"
              onClick={() => handleSelectWeekday(index)}>
              {day}
            </div>
          ))}
        </div>

        <div className={`dates ${direction}`} onMouseLeave={() => { setIsMouseDown(false) }}>
          {calendarRows.map((row, rowIndex) => (
            <React.Fragment key={rowIndex}>
              {row.map((date, dateIndex) => (
                <div
                  key={dateIndex}
                  className={`
                    date 
                    ${isSameMonth(date, currentDate) ? '' : 'disabled'} 
                    ${selectedDates.some(selectedDate => isSameDay(selectedDate, date)) ? 'selected' : ''}`}
                  onMouseDown={() => {
                    setIsMouseDown(true); // indique que j'ai cliqué
                    handleSelectDate(date);
                  }}
                  onMouseOver={() => handleDateHover(date, isMouseDown)}
                  onMouseUp={() => {
                    setIsMouseDown(false);
                  }}
                >
                  {format(date, 'd', { locale: currentLocale })}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
      <Button text='Renitialiser' onClick={handleReset} />
    </div>
  );
};

export default CalendarCustom;
